import {
  LIQUIDITY_NEEDS_MAP,
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_TIME_HORIZON_MAP,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
} from "./accountConstants";

export const TAX_BRACKET_OPTIONS = ["10-12%", "22-24%", "32-35%", "37%+"];

export const TAX_BRACKET_MAP = {
  "10-12%": { start: 10, end: 12 },
  "22-24%": { start: 22, end: 24 },
  "32-35%": { start: 32, end: 35 },
  "37%+": { start: 37, end: 100 },
};

export const BFS_EQUIVALENT_OPTIONS = [
  "Conservative",
  "Moderate Conservative",
  "Moderate",
  "Moderate Growth",
  "Growth",
];

/** Primary investment objective — same strings as API `invest_objectives` / BFS canonical. */
export const PRIMARY_OBJECTIVE_OPTIONS = [
  "Preservation of capital",
  "Income",
  "Growth and Income",
  "Long Term Growth",
  "Speculation",
];

/** Account suitability: `risk_tolerance.time_horizon` (years band). */
export const ACCOUNT_TIME_HORIZON_OPTIONS = [
  "Short Term (Less than 1 Year)",
  "Intermediate (1-7 Years)",
  "Long-Term (7+ Years)",
];

export const ACCOUNT_TIME_HORIZON_MAP = {
  "Short Term (Less than 1 Year)": { start: 0, end: 1 },
  "Intermediate (1-7 Years)": { start: 1, end: 7 },
  "Long-Term (7+ Years)": { start: 7, end: 100 },
};

/** Alias: account risk time horizon dropdowns (not household portfolio bands, not liquidity). */
export const TIME_HORIZON_OPTIONS = ACCOUNT_TIME_HORIZON_OPTIONS;
export const TIME_HORIZON_MAP = ACCOUNT_TIME_HORIZON_MAP;

export const RISK_TOLERANCE_OPTIONS = ["Low", "Moderate", "Moderately High", "High"];

export const SOURCE_OF_FUNDS_OPTIONS = [
  "Income",
  "Gift",
  "Inheritance",
  "Insurance Payout",
  "Social Security Benefits",
  "Sale of Business or Property",
  "Pension/Retirement Savings",
  "Funds from Another Account",
  "Other",
];

/** Purpose of account (use of funds) — same strings as API `use_of_funds` where applicable. */
export const PRIMARY_USE_OPTIONS = [
  "Fund Current/Future Expense",
  "Retirement Savings",
  "Pay for Education: Self or Others",
  "Wealth accumulation",
  "Wealth Transfer/Pass on to Heirs",
  "Other",
];

export const DECISION_MAKING_OPTIONS = [
  "Consults with investment professional",
  "Relies on own research and experience",
  "Relies on help from family and friends",
];

export const DEFAULT_DECISION_MAKING_OPTION = "Consults with investment professional";

/**
 * Display label for stored `risk_tolerance.time_horizon` range.
 * Prefers new suitability bands; falls back to legacy labels for read-only old data.
 */
export function getAccountRiskTimeHorizonLabel(timeHorizonRange, fallback = "") {
  if (!timeHorizonRange || timeHorizonRange.end === null || timeHorizonRange.end === undefined) {
    return fallback;
  }
  const s = Number(timeHorizonRange.start) || 0;
  const e = Number(timeHorizonRange.end) || 0;
  const exact = Object.entries(ACCOUNT_TIME_HORIZON_MAP).find(
    ([, v]) => Number(v.start) === s && Number(v.end) === e,
  );
  if (exact) return exact[0];
  if (s === 0 && e === 0) return "None";
  if (s === 0 && e === 1) return "0-1 Year";
  if (s === 1 && e === 3) return "1-3 Year";
  if (s === 3 && e === 100) return "3+ Year";
  if (e <= 1 && e > 0) return "0-1 Year";
  if (e <= 3) return "1-3 Year";
  if (e <= 7) return "Intermediate (1-7 Years)";
  return "Long-Term (7+ Years)";
}

/**
 * Parse suitability time horizon dropdown → `{ start, end }` for API.
 * Supports new labels and legacy persisted strings.
 */
export function parseAccountRiskTimeHorizonDisplay(displayValue) {
  if (displayValue == null || displayValue === "") return null;
  if (typeof displayValue === "object" && displayValue !== null && "start" in displayValue) {
    return {
      start: Number(displayValue.start),
      end: Number(displayValue.end),
    };
  }
  if (typeof displayValue !== "string") return null;
  const fromMap = ACCOUNT_TIME_HORIZON_MAP[displayValue];
  if (fromMap) return { start: fromMap.start, end: fromMap.end };
  if (displayValue === "None") return { start: 0, end: 0 };
  if (displayValue === "0-1 Year") return { start: 0, end: 1 };
  if (displayValue === "1-3 Year") return { start: 1, end: 3 };
  if (displayValue === "3+ Year") return { start: 3, end: 100 };
  if (displayValue === "1-3 Years") return { start: 1, end: 3 };
  if (displayValue === "3-5 Years") return { start: 3, end: 5 };
  if (displayValue === "5-10 Years") return { start: 5, end: 10 };
  if (displayValue === "10+ Years") return { start: 10, end: 100 };
  return null;
}

export {
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_NEEDS_MAP,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
  LIQUIDITY_TIME_HORIZON_MAP,
};


export const STATUS_EDITOR_ELIGIBLE_SOURCE_STATUSES = [
  "data_collection_completed",
  "forms_generated",
  "docusign_initiated",
  "docusign_completed",
  "account_opened",
  "toa_completed",
  "nigo",
  "under_dispute",
  "firm_paperwork_in_progress",
  "completed",
  "closed",
];


export const STATUS_EDITOR_TARGET_STATUSES = [
  "docusign_initiated",
  "docusign_completed",
  "nigo",
  "under_dispute",
  "account_opened",
  "toa_completed",
  "firm_paperwork_in_progress",
  "completed",
  "closed",
];


export const NIGO_REASON_OPTIONS = [
  { value: "nigo_account_level", label: "Account level" },
  { value: "nigo_service_level", label: "Service level" },
  { value: "nigo_transfer_level", label: "Transfer level" },
];

export const NIGO_REASON_LABELS = NIGO_REASON_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});


export const QUERY_RAISE_ONLY_TARGETS = ["under_dispute"];

export const QUERY_RESOLVE_TARGETS = [
  "created",
  "general_info_completed",
  "household_setup_initiated",
  "active",
  "shared_with_client",
  "shared_with_client_onboarding_done",
  "pending_advisor_review",
  "shared_with_client_for_final_review",
  "advisor_final_review",
  "data_collection_completed",
  "forms_generated",
  "docusign_initiated",
  "docusign_completed",
  "account_opened",
  "toa_completed",
  "nigo",
  "firm_paperwork_in_progress",
  "completed",
  "closed",
];

export const STATUS_LABELS = {
  created: "Created",
  general_info_completed: "General Info Completed",
  household_setup_initiated: "Household Setup Initiated",
  active: "In progress",
  shared_with_client: "With Client",
  shared_with_client_onboarding_done: "Client Onboarding Done",
  pending_advisor_review: "Back with Advisor",
  shared_with_client_for_final_review: "With Client for Final Review",
  advisor_final_review: "Advisor Final Review",
  data_collection_completed: "Data Collection Completed",
  forms_generated: "Forms Generated",
  docusign_initiated: "Signature Execution · Envelope Created",
  docusign_completed: "Signature Execution · Envelope Complete",
  account_opened: "Account Opened",
  toa_completed: "TOA Completed",
  nigo: "NIGO",
  under_dispute: "Query Raised",
  firm_paperwork_in_progress: "Firm Paperwork In Progress",
  completed: "Completed",
  closed: "Closed",
};


export function isStatusEditable(role, status, subStatus) {
  if (!role || !["admin", "ops", "advisor"].includes(String(role).toLowerCase())) return false;
  if (subStatus === "pending_for_execution") return true;
  return Boolean(status) && status !== "inactive";
}


export function getStatusEditorTargets(currentStatus) {
  if (currentStatus === "under_dispute") return QUERY_RESOLVE_TARGETS;
  if (STATUS_EDITOR_ELIGIBLE_SOURCE_STATUSES.includes(currentStatus)) return STATUS_EDITOR_TARGET_STATUSES;
  return QUERY_RAISE_ONLY_TARGETS;
}

export const SOURCE_OF_WEALTH_OPTIONS = [
  "Assets",
  "Divorce",
  "Employment",
  "Entrepreneur",
  "Government Benefits",
  "Inheritance / Gift",
  "Legal Settlement",
  "Lottery / Gaming",
  "Parental Support",
  "Partner / Spouse",
  "Personal Investments",
  "Retirement / Pension",
];
